import React, { useContext, useState, useCallback, useRef } from 'react';

import AnimateHeight from 'react-animate-height';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import AddUserDialog from '../../components/AddUserDialog';

import { ReactComponent as LoadingSpinner } from '../../images/loading-spinner.svg';

import { NavWidthContext, UserContext } from '../../contexts';

import InformationTile from '../CalcInputs/components/InformationTile';

import useFetch from '../../hooks/useFetch';
import { getAllAttributes } from '../../utils/auth';

import './EnterpriseInfo.scss';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const buildCurrentUserFallback = (userAttributes, companyId) => {
  if (!userAttributes?.userId && !userAttributes?.email) return null;
  return {
    accountId: userAttributes.userId || userAttributes.requestUserId || '',
    email: normalizeEmail(userAttributes.email),
    firstName: userAttributes.firstName || '',
    lastName: userAttributes.lastName || '',
    phoneNumber: userAttributes.phoneNumber || '',
    companyId: companyId || userAttributes.enterpriseCompanyId || userAttributes.companyId || '',
    resourceType: 'enterprise account',
    emailVerified: true,
    inEnterpriseUsersGroup: false,
  };
};

const mergeUsersWithCurrentUser = (users, currentUser) => {
  const merged = [...(users || [])];
  if (!currentUser) return merged;

  const currentId = String(currentUser.accountId || '').trim();
  const currentEmail = normalizeEmail(currentUser.email);
  const alreadyPresent = merged.some((user) => {
    const accountId = String(user.accountId || '').trim();
    const email = normalizeEmail(user.email);
    return (currentId && accountId === currentId) || (currentEmail && email === currentEmail);
  });

  if (!alreadyPresent) {
    merged.unshift(currentUser);
  }
  return merged;
};

export default function EnterpriseInfo() {
  const [companyInfoDropdownHeight, setCompanyInfoDropdownHeight] = useState('auto');
  const [additionalUsersDropdownHeight, setAdditionalUsersDropdownHeight] = useState('auto');
  const { companyName, dba, companyId } = JSON.parse(localStorage.getItem('enterpriseCompanyInfo') || '{}');
  const { userData } = useContext(UserContext);
  const currentUserAttributes = userData?.userAttributes || getAllAttributes() || {};

  const [editingCompanyInfo, setEditingCompanyInfo] = useState(false);
  const [companyInfoIsSaving, setCompanyInfoIsSaving] = useState(false);
  const [companyInfo, setNewCompanyInfo] = useState({
    companyName,
    dba,
  });
  const [originalCompanyInfo, setOriginalCompanyInfo] = useState({
    companyName,
    dba,
  });
  const [originalEnterpriseUsers, setOriginalEnterpriseUsers] = useState([]);
  const [addingUser, setAddingUser] = useState(false);
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [usersAreSaving, setUsersAreSaving] = useState(false);
  const [userSaveSuccess, setUserSaveSuccess] = useState(false);
  const [userSaveError, setUserSaveError] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userSavingStates, setUserSavingStates] = useState({});

  const { setNavWidth } = useContext(NavWidthContext);

  const appWidth = useRef(null);

  const appWidthRef = useCallback((node) => {
    if (appWidth?.current) window.removeEventListener('resize', () => setNavWidth(appWidth?.current.scrollWidth));
    if (node) {
      appWidth.current = node;
      window.addEventListener('resize', () => setNavWidth(appWidth?.current.scrollWidth));
    }
  }, []);

  const [, updateCompanyInfoRequest] = useFetch();
  const [, updateUserRequest] = useFetch();

  useFetch({
    url: '/accounts/get-enterprise-users/',
    urlIds: ['enterpriseCompanyId', 'userId'],
    onSuccess: (responseData) => {
      const currentUserFallback = buildCurrentUserFallback(currentUserAttributes, companyId);
      const mergedUsers = mergeUsersWithCurrentUser(responseData || [], currentUserFallback);
      setEnterpriseUsers(mergedUsers.map((user, index) => ({ ...user, id: index })));
      setOriginalEnterpriseUsers(mergedUsers.map((user, index) => ({ ...user, id: index })));
    },
  });

  const handleStartEditing = () => {
    setOriginalCompanyInfo(companyInfo);
    setOriginalEnterpriseUsers(enterpriseUsers);
    setEditingCompanyInfo(true);
  };

  const handleCancelEditing = () => {
    setNewCompanyInfo(originalCompanyInfo);
    setEnterpriseUsers(originalEnterpriseUsers);
    setEditingCompanyInfo(false);
  };

  const handleStartEditingUser = (user) => {
    if (user.resourceType === "temp enterprise account" && !user.emailVerified) {
      setUserSaveError(true);
      return;
    }
    setEditingUserId(user.id);
    setOriginalEnterpriseUsers(enterpriseUsers);
  };

  const handleCancelEditingUser = () => {
    setEnterpriseUsers(originalEnterpriseUsers);
    setEditingUserId(null);
  };

  const handleSaveUser = async (user) => {
    setUserSavingStates(prev => ({ ...prev, [user.id]: true }));
    try {
      const { companyId: enterpriseCompanyId } = JSON.parse(localStorage.getItem('enterpriseCompanyInfo'));
      const payload = {
        enterpriseCompanyId,
        requestUserId: user.accountId,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        email: user.email,
      };
      updateUserRequest({
        url: '/accounts/update-enterprise-user',
        method: 'post',
        body: payload,
        onSuccess: () => {
          setUserSaveSuccess(true);
          setEditingUserId(null);
          setUserSavingStates(prev => ({ ...prev, [user.id]: false }));
        },
        onError: () => {
          setUserSaveError(true);
          setUserSavingStates(prev => ({ ...prev, [user.id]: false }));
        }
      });
    } catch (error) {
      setUserSaveError(true);
      setUserSavingStates(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleSaveCompanyInfo = async () => {
    setCompanyInfoIsSaving(true);
    setUserSaveSuccess(false);
    setUserSaveError(false);
    try {
      updateCompanyInfoRequest({
        url: '/companies/enterprise/update-enterprise-company-info',
        method: 'post',
        body: {
          companyId,
          companyName: companyInfo.companyName,
          dba: companyInfo.dba,
        },
        onSuccess: () => {
          const updatedCompanyInfo = {
            ...JSON.parse(localStorage.getItem('enterpriseCompanyInfo')),
            companyName: companyInfo.companyName,
            dba: companyInfo.dba,
          };
          localStorage.setItem('enterpriseCompanyInfo', JSON.stringify(updatedCompanyInfo));
          setEditingCompanyInfo(false);
          setUserSaveSuccess(true);
        },
        onError: () => {
          setUserSaveError(true);
        },
        onFinally: () => {
          setCompanyInfoIsSaving(false);
        }
      });
    } catch (error) {
      setUserSaveError(true);
      setCompanyInfoIsSaving(false);
    }
  };

  const isAnyUserEditing = () => {
    return editingUserId !== null || Object.values(userSavingStates).some(state => state === true);
  };

  return (
    <main className="EnterpriseInfo" ref={appWidthRef}>
      <div className="page-header">
        <h4>{companyName}</h4>
        <Button
          onClick={() => {
            setAddingUser(true);
          }}
        >
          <AddIcon />
          Add user
        </Button>
      </div>
      <div className="profile-settings-container">
        <div className="info-dropdown">
          <div
            role="button"
            className="header-row"
            onClick={() => { setCompanyInfoDropdownHeight(companyInfoDropdownHeight ? 0 : 'auto'); }}
            onKeyDown={(e) => { if (e.key === 'Enter') setCompanyInfoDropdownHeight(companyInfoDropdownHeight ? 0 : 'auto'); }}
            tabIndex={0}
          >
            <IconButton
              className="chevron-btn"
              onClick={() => { setCompanyInfoDropdownHeight(companyInfoDropdownHeight ? 0 : 'auto'); }}
            >
              <ExpandCircleDownOutlinedIcon className={`circle-chevron${companyInfoDropdownHeight ? ' upward' : ' downward'}`} />
            </IconButton>
            <h5>Company Info</h5>
            {!editingCompanyInfo && !companyInfoIsSaving && !isAnyUserEditing() ? (
              <IconButton
                className="edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEditing();
                }}
              >
                <EditIcon />
              </IconButton>
            ) : editingCompanyInfo ? (
              <div className="btns-container">
                <Button
                  className="save-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveCompanyInfo();
                  }}
                  disabled={companyInfoIsSaving}
                >
                  {!companyInfoIsSaving ? (
                    <>
                      <SaveOutlinedIcon />
                      Save
                    </>
                  ) : (
                    <>
                      <LoadingSpinner className="loading-spinner" />
                      Saving...
                    </>
                  )}
                </Button>
                <Button
                  className="cancel-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelEditing();
                  }}
                  disabled={companyInfoIsSaving}
                >
                  <CancelOutlinedIcon />
                  Cancel
                </Button>
              </div>
            ) : null}
          </div>
          <div>
            <AnimateHeight duration={500} height={companyInfoDropdownHeight}>
              <div className="data-tiles-grid">
                <InformationTile
                  label="Company name"
                  dataName="companyName"
                  isEditing={editingCompanyInfo}
                  values={companyInfo}
                  setNewValues={setNewCompanyInfo}
                />
                <InformationTile
                  label="Doing Business As (DBA)"
                  dataName="dba"
                  isEditing={editingCompanyInfo}
                  values={companyInfo}
                  setNewValues={setNewCompanyInfo}
                />
              </div>
            </AnimateHeight>
          </div>
        </div>
        <hr />
        <div className="info-dropdown">
          <div
            role="button"
            className="header-row"
            onClick={() => { setAdditionalUsersDropdownHeight(additionalUsersDropdownHeight ? 0 : 'auto'); }}
            onKeyDown={(e) => { if (e.key === 'Enter') setAdditionalUsersDropdownHeight(additionalUsersDropdownHeight ? 0 : 'auto'); }}
            tabIndex={0}
          >
            <IconButton
              className="chevron-btn"
              onClick={() => { setAdditionalUsersDropdownHeight(additionalUsersDropdownHeight ? 0 : 'auto'); }}
            >
              <ExpandCircleDownOutlinedIcon
                className={`circle-chevron${additionalUsersDropdownHeight ? ' upward' : ' downward'}`}
              />
            </IconButton>
            <h5>Additional users</h5>
          </div>
          <div>
            <AnimateHeight duration={500} height={additionalUsersDropdownHeight}>
              <div className="data-tiles-grid users">
                {enterpriseUsers.length > 0 ? (
                  <>
                    {enterpriseUsers.map((user) => (
                      <React.Fragment key={user.id}>
                        <div className="user-header">
                          {editingUserId !== user.id && !userSavingStates[user.id] && !isAnyUserEditing() ? (
                            <IconButton
                              className="edit-btn"
                              onClick={() => handleStartEditingUser(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          ) : editingUserId === user.id || userSavingStates[user.id] ? (
                            <div className="btns-container">
                              <Button
                                className="save-btn"
                                onClick={() => handleSaveUser(user)}
                                disabled={userSavingStates[user.id]}
                              >
                                {!userSavingStates[user.id] ? (
                                  <>
                                    <SaveOutlinedIcon />
                                    Save
                                  </>
                                ) : (
                                  <>
                                    <LoadingSpinner className="loading-spinner" />
                                    Saving...
                                  </>
                                )}
                              </Button>
                              <Button
                                className="cancel-btn"
                                onClick={handleCancelEditingUser}
                                disabled={userSavingStates[user.id]}
                              >
                                <CancelOutlinedIcon />
                                Cancel
                              </Button>
                            </div>
                          ) : null}
                        </div>
                        <InformationTile
                          label="First name"
                          dataName="firstName"
                          isEditing={editingUserId === user.id}
                          values={user}
                          setNewValues={(newUser) => {
                            setEnterpriseUsers((prevUsers) =>
                              prevUsers.map((u) => (u.id === user.id ? { ...u, ...newUser } : u))
                            );
                          }}
                        />
                        <InformationTile
                          label="Last name"
                          dataName="lastName"
                          isEditing={editingUserId === user.id}
                          values={user}
                          setNewValues={(newUser) => {
                            setEnterpriseUsers((prevUsers) =>
                              prevUsers.map((u) => (u.id === user.id ? { ...u, ...newUser } : u))
                            );
                          }}
                        />
                        <InformationTile
                          label="Email"
                          dataName="email"
                          isEditing={false}
                          values={user}
                          setNewValues={(newUser) => {
                            setEnterpriseUsers((prevUsers) =>
                              prevUsers.map((u) => (u.id === user.id ? { ...u, ...newUser } : u))
                            );
                          }}
                          badge={
                            <Chip
                              label={String(user.resourceType || '').startsWith('temp ') ? 'Unverified' : 'Verified'}
                              className={String(user.resourceType || '').startsWith('temp ') ? 'chip-temp' : 'chip-perm'}
                              size="small"
                              sx={{ height: '20px', fontWeight: 600, marginLeft: '8px', fontSize: '0.95em', verticalAlign: 'middle' }}
                            />
                          }
                        />
                        <InformationTile
                          label="Phone number"
                          dataName="phoneNumber"
                          isEditing={editingUserId === user.id}
                          values={user}
                          setNewValues={(newUser) => {
                            setEnterpriseUsers((prevUsers) =>
                              prevUsers.map((u) => (u.id === user.id ? { ...u, ...newUser } : u))
                            );
                          }}
                        />
                        <hr className="user-divider" />
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  <p className="empty-users-message">No additional enterprise users found.</p>
                )}
              </div>
            </AnimateHeight>
          </div>
        </div>
        <hr />
      </div>
      <AddUserDialog addingUser={addingUser} setAddingUser={setAddingUser} setEnterpriseUsers={setEnterpriseUsers} />
      <Snackbar
        open={userSaveSuccess}
        autoHideDuration={3000}
        onClose={() => setUserSaveSuccess(false)}
        message="Changes saved successfully!"
      />
      <Snackbar
        open={userSaveError}
        autoHideDuration={3000}
        onClose={() => setUserSaveError(false)}
        message="Cannot edit unverified users."
        color="error"
      />
    </main>
  );
}